import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/**
 * Reusable mobile pagination (`< 1 2 3 >`).
 *
 * Controlled component: `page` is 1-indexed. Selected page is a filled blue
 * square; the rest are outlined. Prev/next chevrons disable at the ends.
 *
 * @param {number} count            total number of pages
 * @param {number} page             current page (1-indexed)
 * @param {(page:number)=>void} onChange
 * @param {number} [siblingCount]   page numbers to show either side of current
 */
export default function Pagination({ count, page, onChange, siblingCount = 1 }) {
  if (!count || count < 1) return null;

  const go = (p) => {
    const next = Math.min(Math.max(p, 1), count);
    if (next !== page) onChange(next);
  };

  // Windowed page numbers so long lists don't overflow the screen.
  const start = Math.max(1, Math.min(page - siblingCount, count - (siblingCount * 2 + 1) + 1));
  const end = Math.min(count, start + (siblingCount * 2 + 1) - 1);
  const pages = [];
  for (let p = start; p <= end; p += 1) pages.push(p);

  const squareSx = (selected) => ({
    minWidth: 34,
    height: 34,
    px: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 1.5,
    cursor: "pointer",
    border: "1px solid",
    borderColor: selected ? "primary.clr-300" : "neutral.clr-200",
    bgcolor: selected ? "primary.clr-300" : "transparent",
  });

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <IconButton
        size="small"
        disabled={page <= 1}
        onClick={() => go(page - 1)}
        sx={{ border: "1px solid", borderColor: "neutral.clr-200", borderRadius: 1.5 }}
        aria-label="previous page"
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>

      {pages.map((p) => {
        const selected = p === page;
        return (
          <Box key={p} onClick={() => go(p)} sx={squareSx(selected)}>
            <Typography
              variant={selected ? "body02-bold" : "body02-medium"}
              color={selected ? "shades.clr-white-900" : "neutral.clr-700"}
            >
              {p}
            </Typography>
          </Box>
        );
      })}

      <IconButton
        size="small"
        disabled={page >= count}
        onClick={() => go(page + 1)}
        sx={{ border: "1px solid", borderColor: "neutral.clr-200", borderRadius: 1.5 }}
        aria-label="next page"
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
